/**
 * Key holding the timestamp of the last account reactivation.
 * The welcome message in the feed is shown for 2 hours after that timestamp
 * and the key is removed when the user closes the message.
 */
export const REACTIVATED_USER_TIMESTAMP_KEY = "reactivatedUserTimestamp";

/**
 * Key holding the id of the question currently on stage, read by
 * usePostUserFeedback into every report's "Question ID" line.
 *
 * This key is shared with qset v3 (`_switchToQuestion()` in
 * `apps/minute-qset/src/qset-questions.js` writes it as a bare string on every
 * stage transition), so it is written and read as a raw string here too --
 * never through `setLocalStorageItem`/
 * `getLocalStorageItem`, which JSON-encode (`"q1"` with quotes) and would break
 * that interop, and never through `localDataStore`/`localDataGet`, which add a
 * TTL envelope this key does not use.
 */
const CURRENT_QUESTION_ID_KEY = "currentQuestionId";

export enum LocalStorageErrorType {
  NOT_AVAILABLE = "NOT_AVAILABLE",
  QUOTA_EXCEEDED = "QUOTA_EXCEEDED",
  SECURITY_ERROR = "SECURITY_ERROR",
  PARSE_ERROR = "PARSE_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export class LocalStorageError extends Error {
  constructor(
    public type: LocalStorageErrorType,
    message: string,
    public originalError?: Error,
  ) {
    super(message);
    this.name = "LocalStorageError";
  }
}

/**
 * Remove data items with expired TTL from localStorage.
 *
 * @param keyPattern - pattern to match keys in localStorage
 */
export const localDataGarbageCollect = (keyPattern: string) => {
  const items = {...window.localStorage};

  for (const [key, value] of Object.entries(items)) {
    if (key.includes(keyPattern)) {
      let item;
      try {
        item = JSON.parse(value);
      } catch {
        item = null;
      }

      if (item && Date.now() > item.ttl) {
        window.localStorage.removeItem(key);
      }
    }
  }
};

/*
 * Store data in localStorage with time to live.
 *
 * @param keyName - key name for localStorage
 * @param keyValue - value to store
 * @param ttl - time to live in seconds (default 600)
 */
export const localDataStore = (
  keyName: string,
  keyValue: string | object,
  ttl = 600,
) => {
  const data = {
    value: keyValue,
    ttl: Date.now() + ttl * 1000,
  };

  window.localStorage.setItem(keyName, JSON.stringify(data));
};

/*
 * Get data from localStorage.
 * If the data is expired, remove it. If the data is not found, return null.
 *
 * @param keyName - key name for localStorage
 */
export const localDataGet = (keyName: string) => {
  const data = window.localStorage.getItem(keyName);

  if (!data) return null;

  let item;
  try {
    item = JSON.parse(data);
  } catch {
    return null;
  }

  if (Date.now() > item.ttl) {
    window.localStorage.removeItem(keyName);
    return null;
  }

  return item.value;
};

// key prefix for resume data in localStorage
const resumeDataKeyPrefix = "resumeData:";

/**
 * Get key for resume data in localStorage.
 *
 * The qset version is part of the key because v3 and v4 do not share an
 * `Answer` shape. Cloudflare version affinity only holds for the lifetime of a
 * gradual deployment, so a device can be moved to the other version mid-survey
 * by a rollback, a completed rollout, or an expired cookie; namespacing the key
 * makes that restart from question one rather than feed v3 data into v4.
 */
export const resumeDataKey = (pollAsset: string, qsetVersion: number) => {
  return `${resumeDataKeyPrefix}v${qsetVersion}:${pollAsset}`;
};

/**
 * Remove resume data item from localStorage.
 */
export const resumeDataRemove = (pollAsset: string, qsetVersion: number) => {
  window.localStorage.removeItem(resumeDataKey(pollAsset, qsetVersion));
};

/**
 * Remove all resume data items with expired TTL from localStorage.
 */
export const resumeDataGarbageCollect = () => {
  localDataGarbageCollect(resumeDataKeyPrefix);
};

/**
 * Store resume data in localStorage.
 * TTL is set to 600 seconds (10 minutes) by default.
 * If an user returns to the questionnaire after a long time, he should start from the beginning.
 * This is to prevent the user from filling in the questionnaire with a long delay,
 * which could lead to incorrect results.
 */
export const resumeDataStore = (
  pollAsset: string,
  qsetVersion: number,
  data: object,
) => {
  localDataStore(resumeDataKey(pollAsset, qsetVersion), data);
};

/**
 * How long the resume record lives once the survey has reached its end and the
 * finish is being saved (#1417): a reload or a return after closing the tab
 * must resume there and save again, not start fresh and delete the answers.
 */
export const RESUME_FINISH_TTL_S = 24 * 60 * 60;

/**
 * Writes the resume record for a finished survey with the long finish TTL.
 * Written, not extended: the stored record may already have run out (a qset
 * without an outro is on its last question for longer than the 10-minute
 * window), and `localDataGet` deletes an expired record, so extending it would
 * silently do nothing.
 */
export const resumeDataStoreForFinish = (
  pollAsset: string,
  qsetVersion: number,
  data: object,
) => {
  localDataStore(
    resumeDataKey(pollAsset, qsetVersion),
    data,
    RESUME_FINISH_TTL_S,
  );
};

/**
 * Get resume data from localStorage.
 */
export const resumeDataGet = (pollAsset: string, qsetVersion: number) => {
  return localDataGet(resumeDataKey(pollAsset, qsetVersion));
};

/**
 * Identity that a resume record belongs to.
 *
 * `targetingId` is set only for introsets — see `resumeAssignmentId`. It is
 * nullable because the value is read back from JSON and from the API, either of
 * which may yield null rather than an absent key.
 */
export interface ResumeIdentity {
  userId: string;
  pollSlug: string;
  targetingId?: number | null;
}

/**
 * The assignment a resume record belongs to, or undefined when the poll has no
 * per-assignment identity.
 *
 * The backend sends `targetingId` for classic polls too, so its presence does
 * not mark an introset. What separates them is where the result is written:
 * classic results go to `/results/<pollSlug>/<userId>`, which carries no
 * targetingId, so a re-assigned classic poll keeps writing to the same node and
 * resuming across it loses nothing. Only introsets get a fresh result node per
 * assignment, so only they must start over (#954).
 */
export const resumeAssignmentId = (poll?: {
  type?: string;
  targetingId?: number | null;
}) => (poll?.type === "introset" ? poll.targetingId : undefined);

/**
 * Whether stored resume data belongs to the given identity.
 *
 * Resume data is keyed by qset asset only, which is shared across re-assignments
 * of the same introset. A re-assignment issues a new targetingId and a new result
 * node, so without this check the abandoned attempt from the previous assignment
 * would be resumed instead of starting over (#954).
 */
export const resumeDataMatches = (
  storedData: ResumeIdentity | null,
  identity: ResumeIdentity,
): boolean => {
  if (!storedData) return false;
  if (storedData.userId !== identity.userId) return false;
  if (storedData.pollSlug !== identity.pollSlug) return false;

  // Classic polls have no targetingId. Normalise so a null coming back from the
  // API never reads as a different assignment than a missing one.
  return (
    (storedData.targetingId ?? undefined) ===
    (identity.targetingId ?? undefined)
  );
};

/**
 * Check if localStorage is available in the current environment.
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__localStorage_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get an item from localStorage with error handling.
 */
export const getLocalStorageItem = (key: string) => {
  // Check if localStorage is available
  if (!isLocalStorageAvailable()) {
    throw new LocalStorageError(
      LocalStorageErrorType.NOT_AVAILABLE,
      "localStorage není dostupná. Může to být způsobeno soukromým režimem prohlížení nebo omezeními prohlížeče.",
    );
  }

  try {
    const item = localStorage.getItem(key);
    if (item === null) return null;

    // Try to parse as JSON
    try {
      return JSON.parse(item);
    } catch (parseError) {
      // If parsing fails, throw parse error but still return the string value
      console.warn(
        `Nepodařilo se parsovat položku localStorage "${key}" jako JSON:`,
        parseError,
      );
      throw new LocalStorageError(
        LocalStorageErrorType.PARSE_ERROR,
        `Nepodařilo se parsovat položku localStorage "${key}" jako JSON`,
        parseError as Error,
      );
    }
  } catch (error) {
    // Handle specific localStorage errors
    if (error instanceof LocalStorageError) {
      throw error;
    }

    if (error instanceof DOMException) {
      switch (error.name) {
        case "QuotaExceededError":
        case "NS_ERROR_DOM_QUOTA_REACHED":
          throw new LocalStorageError(
            LocalStorageErrorType.QUOTA_EXCEEDED,
            "Překročena kvóta localStorage. Disk může být plný nebo bylo dosaženo limitu úložiště.",
            error,
          );
        case "SecurityError":
          throw new LocalStorageError(
            LocalStorageErrorType.SECURITY_ERROR,
            "Bezpečnostní chyba při přístupu k localStorage. Může to být způsobeno nastavením zabezpečení prohlížeče.",
            error,
          );
        default:
          throw new LocalStorageError(
            LocalStorageErrorType.UNKNOWN_ERROR,
            `Neznámá chyba při přístupu k localStorage: ${error.message}`,
            error,
          );
      }
    }

    throw new LocalStorageError(
      LocalStorageErrorType.UNKNOWN_ERROR,
      `Neočekávaná chyba při přístupu k localStorage: ${error}`,
      error as Error,
    );
  }
};

/**
 * Store the id of the question currently on stage.
 *
 * Wrapped in try/catch and swallowed, matching v3's own access to this key
 * (`_switchToQuestion()` in `qset-questions.js`): a blocked or unavailable
 * localStorage (private browsing, disabled storage) must never break the survey
 * or the feedback send.
 */
export const currentQuestionIdStore = (id: string): void => {
  try {
    window.localStorage.setItem(CURRENT_QUESTION_ID_KEY, id);
  } catch {
    // Swallowed: see JSDoc.
  }
};

/**
 * Remove the current-question id, e.g. on qset dismiss, mount or unmount.
 * See `currentQuestionIdStore` for why errors are swallowed.
 */
export const currentQuestionIdRemove = (): void => {
  try {
    window.localStorage.removeItem(CURRENT_QUESTION_ID_KEY);
  } catch {
    // Swallowed: see JSDoc.
  }
};

/**
 * Read the current-question id, or null if unset/unavailable.
 * See `currentQuestionIdStore` for why errors are swallowed.
 */
export const currentQuestionIdGet = (): string | null => {
  try {
    return window.localStorage.getItem(CURRENT_QUESTION_ID_KEY);
  } catch {
    return null;
  }
};

/**
 * Set an item in localStorage with error handling.
 */
export const setLocalStorageItem = (key: string, value: unknown): void => {
  if (!isLocalStorageAvailable()) {
    throw new LocalStorageError(
      LocalStorageErrorType.NOT_AVAILABLE,
      "localStorage není dostupná. Může to být způsobeno soukromým režimem prohlížení nebo omezeními prohlížeče.",
    );
  }

  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      throw new LocalStorageError(
        LocalStorageErrorType.QUOTA_EXCEEDED,
        "Nelze uložit do localStorage: překročena kvóta. Diskový prostor může být plný.",
        error,
      );
    }
    throw error;
  }
};
